/**
 * Drive Database - Real-World Drive Specifications
 *
 * DISCLAIMER: This data was generated by an AI based on general knowledge of drive
 * specifications from manufacturer datasheets and publicly available information.
 *
 * Data Sources:
 * - Technical specifications (capacity, RPM, power, etc.): Based on manufacturer specs
 * - AFR values: From manufacturer specifications or Backblaze statistics when available
 * - Pricing: NOT INCLUDED - Prices vary by region, retailer, and change frequently
 *
 * IMPORTANT: For accurate planning, please verify specifications against current
 * manufacturer datasheets and enter your own pricing.
 *
 * Contributors are encouraged to submit corrections and additions with sources.
 */

import type { DriveSpec } from '../types';

/**
 * Comprehensive drive database
 */
export const DRIVE_DATABASE: DriveSpec[] = [
  // ============================================================================
  // SEAGATE EXOS (Enterprise HDD)
  // ============================================================================
  {
    manufacturer: 'Seagate',
    model: 'Exos X20 20TB',
    // Source: https://www.seagate.com/files/www-content/datasheets/pdfs/exos-x20-channel-DS2080-2111GB-en_EM.pdf (verified 2026-02-08)
    capacityTB: 20,
    rpm: 7200,
    interface: 'SAS',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 550,  // 4K QD16 write from datasheet
    peakSequentialMBps: 285,
    wattsIdle: 5.8,  // Idle A (SAS model)
    wattsLoad: 9.8,  // Max operating (SAS model)
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Seagate',
    model: 'Exos X18 16TB',
    // Source: https://www.seagate.com/files/www-content/datasheets/pdfs/exos-x18-channel-DS2045-1-2007GB-en_SG.pdf (verified 2026-02-08)
    capacityTB: 16,
    rpm: 7200,
    interface: 'SAS',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 550,  // 4K QD16 write from datasheet
    peakSequentialMBps: 261,
    wattsIdle: 5.8,  // Idle A (SAS model)
    wattsLoad: 9.9,  // Max operating random read (SAS model)
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Seagate',
    model: 'Exos X16 14TB',
    // Source: https://www.seagate.com/www-content/datasheets/pdfs/exos-x16-DS2011-1-1904US-en_US.pdf (verified 2026-02-08)
    capacityTB: 14,
    rpm: 7200,
    interface: 'SAS',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 440,  // 4K QD16 write from datasheet
    peakSequentialMBps: 261,
    wattsIdle: 5.0,  // Idle A (SAS model)
    wattsLoad: 10.2,  // Max operating random read (SAS model)
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Seagate',
    model: 'Exos X20 20TB SATA',
    // Source: https://www.seagate.com/content/dam/seagate/migrated-assets/www-content/product-content/enterprise-hdd-fam/exos-x20-channel/en-us/docs/200562700b.pdf (verified 2026-02-08)
    capacityTB: 20,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 550,  // 4K QD16 write from datasheet
    peakSequentialMBps: 285,
    wattsIdle: 5.6,  // Idle A
    wattsLoad: 9.3,  // Operating random read 4K16Q
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },

  // ============================================================================
  // SEAGATE IRONWOLF (NAS HDD)
  // ============================================================================
  {
    manufacturer: 'Seagate',
    model: 'IronWolf Pro 20TB',
    // Source: https://www.seagate.com/www-content/datasheets/pdfs/ironwolf-pro-20tb-DS1914-21-2206US-en_US.pdf (verified 2026-02-08)
    capacityTB: 20,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 285,
    wattsIdle: 5.5,
    wattsLoad: 7.7,
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Seagate',
    model: 'IronWolf Pro 16TB',
    // Source: https://www.seagate.com/www-content/datasheets/pdfs/ironwolf-pro-20tb-DS1914-21-2206US-en_US.pdf (verified 2026-02-08)
    capacityTB: 16,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 270,
    wattsIdle: 5.0,
    wattsLoad: 7.6,
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Seagate',
    model: 'IronWolf 12TB',
    // Source: https://www.seagate.com/www-content/datasheets/pdfs/ironwolf-12tbDS1904-9-1707US-en_US.pdf (verified 2026-02-08)
    capacityTB: 12,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 80,
    peakSequentialMBps: 210,
    wattsIdle: 5.0,
    wattsLoad: 7.8,
    afr: 1.0,  // From datasheet (1M MTBF = ~1.0% AFR)
  },
  {
    manufacturer: 'Seagate',
    model: 'IronWolf 8TB',
    // Source: https://www.seagate.com/content/dam/seagate/migrated-assets/www-content/product-content/ironwolf/en-us/docs/100804010b.pdf (verified 2026-02-08)
    capacityTB: 8,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 75,
    peakSequentialMBps: 210,
    wattsIdle: 4.4,  // Idle A
    wattsLoad: 8.4,  // Operating random read
    afr: 0.88,  // From datasheet (1M MTBF = 0.88% AFR)
  },

  // ============================================================================
  // WESTERN DIGITAL RED PLUS/PRO (NAS HDD)
  // ============================================================================
  {
    manufacturer: 'Western Digital',
    model: 'Red Plus 12TB',
    // Source: https://documents.westerndigital.com/content/dam/doc-library/en_us/assets/public/western-digital/product/internal-drives/wd-red-plus-hdd/data-sheet-western-digital-wd-red-plus-hdd.pdf (verified 2026-02-08)
    capacityTB: 12,
    rpm: 7200,  // Actual 7200 (datasheet notes: "ID Device may report 5400 to reflect previous Performance Class")
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 80,
    peakSequentialMBps: 260,  // from datasheet
    wattsIdle: 6.1,  // from datasheet
    wattsLoad: 8.8,  // from datasheet
    afr: 0.88,  // From datasheet (1M MTBF = 0.88% AFR)
  },
  {
    manufacturer: 'Western Digital',
    model: 'Red Plus 8TB',
    // Source: https://documents.westerndigital.com/content/dam/doc-library/en_us/assets/public/western-digital/product/internal-drives/wd-red-plus-hdd/data-sheet-western-digital-wd-red-plus-hdd.pdf (verified 2026-02-08)
    capacityTB: 8,
    rpm: 5640,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 75,
    peakSequentialMBps: 215,  // from datasheet
    wattsIdle: 4.4,  // from datasheet
    wattsLoad: 5.8,  // from datasheet
    afr: 0.88,  // From datasheet (1M MTBF = 0.88% AFR)
  },
  {
    manufacturer: 'Western Digital',
    model: 'Red Pro 18TB',
    // Source: https://documents.westerndigital.com/content/dam/doc-library/en_us/assets/public/western-digital/product/internal-drives/wd-red-pro-hdd/data-sheet-wd-red-pro-hdd.pdf (verified 2026-02-08)
    capacityTB: 18,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 90,
    peakSequentialMBps: 272,  // from datasheet
    wattsIdle: 3.6,  // from datasheet
    wattsLoad: 6.1,  // from datasheet
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Western Digital',
    model: 'Red Pro 14TB',
    // Source: https://documents.westerndigital.com/content/dam/doc-library/en_us/assets/public/western-digital/product/internal-drives/wd-red-pro-hdd/data-sheet-wd-red-pro-hdd.pdf (verified 2026-02-08)
    capacityTB: 14,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 90,
    peakSequentialMBps: 255,  // from datasheet
    wattsIdle: 3.0,  // from datasheet
    wattsLoad: 6.2,  // from datasheet
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },

  // ============================================================================
  // TOSHIBA MG SERIES (Enterprise HDD)
  // ============================================================================
  {
    manufacturer: 'Toshiba',
    model: 'MG09 18TB',
    // Source: https://toshiba.semicon-storage.com/content/dam/toshiba-ss-v3/master/en/storage/product/data-center-enterprise/MG09_Product_Overview_rev.1.pdf (verified 2026-02-08)
    capacityTB: 18,
    rpm: 7200,
    interface: 'SAS',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 281,  // 268 MiB/s from datasheet
    wattsIdle: 4.49,  // Active Idle SAS from datasheet
    wattsLoad: 8.71,  // Write/Read SAS from datasheet
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Toshiba',
    model: 'MG09 16TB',
    // Source: https://toshiba.semicon-storage.com/content/dam/toshiba-ss-v3/master/en/storage/product/data-center-enterprise/MG09_Product_Overview_rev.1.pdf (verified 2026-02-08)
    capacityTB: 16,
    rpm: 7200,
    interface: 'SAS',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 281,  // 268 MiB/s from datasheet
    wattsIdle: 4.25,  // Active Idle SAS from datasheet
    wattsLoad: 8.40,  // Write/Read SAS from datasheet
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Toshiba',
    model: 'MG08 14TB',
    // Source: https://storage.toshiba.com/docs/enterprise-hdd-documents/ehdd-mg08_product-overview_rev-3.pdf (verified 2026-02-08)
    capacityTB: 14,
    rpm: 7200,
    interface: 'SAS',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 274,  // 262 MiB/s from datasheet
    wattsIdle: 4.46,  // Active Idle from datasheet
    wattsLoad: 8.12,  // Random Write/Read from datasheet
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },
  {
    manufacturer: 'Toshiba',
    model: 'MG09 12TB',
    // Source: https://toshiba.semicon-storage.com/content/dam/toshiba-ss-v3/master/en/storage/product/data-center-enterprise/MG09_Product_Overview_rev.1.pdf (verified 2026-02-08)
    // Note: MG08 series does not have a 12TB model; corrected to MG09 12TB
    capacityTB: 12,
    rpm: 7200,
    interface: 'SAS',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 281,  // 268 MiB/s from datasheet
    wattsIdle: 3.30,  // Active Idle from datasheet
    wattsLoad: 7.43,  // Write/Read from datasheet
    afr: 0.35,  // From datasheet (2.5M MTBF = 0.35% AFR)
  },

  // ============================================================================
  // TOSHIBA N300 (NAS HDD)
  // ============================================================================
  {
    manufacturer: 'Toshiba',
    model: 'N300 16TB',
    // Source: https://toshiba.semicon-storage.com/content/dam/toshiba-ss-v3/master/en/storage/product/internal-specialty/HDD-NSENA.pdf (verified 2026-02-08)
    capacityTB: 16,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 281,  // 268 MiB/s from datasheet
    wattsIdle: 4.41,  // Active Idle from datasheet
    wattsLoad: 7.48,  // Operating from datasheet
    afr: 0.73,  // From datasheet (1.2M MTBF = 0.73% AFR)
  },
  {
    manufacturer: 'Toshiba',
    model: 'N300 12TB',
    // Source: https://toshiba.semicon-storage.com/content/dam/toshiba-ss-v3/master/en/storage/product/internal-specialty/HDD-NSENA.pdf (verified 2026-02-08)
    capacityTB: 12,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 85,
    peakSequentialMBps: 281,  // 268 MiB/s from datasheet
    wattsIdle: 3.3,  // Active Idle from datasheet
    wattsLoad: 6.49,  // Operating from datasheet
    afr: 0.73,  // From datasheet (1.2M MTBF = 0.73% AFR)
  },
  {
    manufacturer: 'Toshiba',
    model: 'N300 8TB',
    // Source: https://toshiba.semicon-storage.com/content/dam/toshiba-ss-v3/master/en/storage/product/internal-specialty/HDD-NSENA.pdf (verified 2026-02-08)
    capacityTB: 8,
    rpm: 7200,
    interface: 'SATA',
    type: 'HDD',
    recordingType: 'CMR',
    iops: 80,
    peakSequentialMBps: 260,  // 248 MiB/s from datasheet
    wattsIdle: 4.92,  // Active Idle from datasheet
    wattsLoad: 8.19,  // Operating from datasheet
    afr: 0.88,  // From datasheet (1M MTBF = 0.88% AFR)
  },

  // ============================================================================
  // SATA SSDS
  // ============================================================================
  {
    manufacturer: 'Samsung',
    model: '870 EVO 4TB',
    capacityTB: 4,
    rpm: 0,
    interface: 'SATA',
    type: 'SSD',
    recordingType: null,
    iops: 68000,
    peakSequentialMBps: 560,
    wattsIdle: 2.5,
    wattsLoad: 4.0,
    afr: 0.50,
  },
  {
    manufacturer: 'Samsung',
    model: '870 EVO 2TB',
    capacityTB: 2,
    rpm: 0,
    interface: 'SATA',
    type: 'SSD',
    recordingType: null,
    iops: 68000,
    peakSequentialMBps: 560,
    wattsIdle: 2.0,
    wattsLoad: 3.5,
    afr: 0.50,
  },
  {
    manufacturer: 'Samsung',
    model: '870 QVO 4TB',
    capacityTB: 4,
    rpm: 0,
    interface: 'SATA',
    type: 'SSD',
    recordingType: null,
    iops: 54000,
    peakSequentialMBps: 560,
    wattsIdle: 2.5,
    wattsLoad: 4.0,
    afr: 0.58,
  },
  {
    manufacturer: 'Crucial',
    model: 'MX500 4TB',
    capacityTB: 4,
    rpm: 0,
    interface: 'SATA',
    type: 'SSD',
    recordingType: null,
    iops: 62000,
    peakSequentialMBps: 560,
    wattsIdle: 2.2,
    wattsLoad: 3.8,
    afr: 0.55,
  },
  {
    manufacturer: 'Crucial',
    model: 'MX500 2TB',
    capacityTB: 2,
    rpm: 0,
    interface: 'SATA',
    type: 'SSD',
    recordingType: null,
    iops: 62000,
    peakSequentialMBps: 560,
    wattsIdle: 1.8,
    wattsLoad: 3.2,
    afr: 0.55,
  },

  // ============================================================================
  // NVMe SSDS
  // ============================================================================
  {
    manufacturer: 'Samsung',
    model: '970 EVO Plus 2TB',
    capacityTB: 2,
    rpm: 0,
    interface: 'NVMe',
    type: 'SSD',
    recordingType: null,
    iops: 120000,
    peakSequentialMBps: 3500,
    wattsIdle: 3.0,
    wattsLoad: 5.5,
    afr: 0.45,
  },
  {
    manufacturer: 'Samsung',
    model: '970 EVO Plus 1TB',
    capacityTB: 1,
    rpm: 0,
    interface: 'NVMe',
    type: 'SSD',
    recordingType: null,
    iops: 120000,
    peakSequentialMBps: 3500,
    wattsIdle: 2.5,
    wattsLoad: 5.0,
    afr: 0.45,
  },
  {
    manufacturer: 'Samsung',
    model: '980 Pro 2TB',
    capacityTB: 2,
    rpm: 0,
    interface: 'NVMe',
    type: 'SSD',
    recordingType: null,
    iops: 140000,
    peakSequentialMBps: 7000,
    wattsIdle: 3.5,
    wattsLoad: 6.5,
    afr: 0.40,
  },
  {
    manufacturer: 'Western Digital',
    model: 'Black SN850X 2TB',
    capacityTB: 2,
    rpm: 0,
    interface: 'NVMe',
    type: 'SSD',
    recordingType: null,
    iops: 135000,
    peakSequentialMBps: 7300,
    wattsIdle: 3.2,
    wattsLoad: 6.0,
    afr: 0.42,
  },
  {
    manufacturer: 'Crucial',
    model: 'P5 Plus 2TB',
    capacityTB: 2,
    rpm: 0,
    interface: 'NVMe',
    type: 'SSD',
    recordingType: null,
    iops: 110000,
    peakSequentialMBps: 6600,
    wattsIdle: 3.0,
    wattsLoad: 5.8,
    afr: 0.48,
  },
];

// Helper function to get drives by manufacturer
export function getDrivesByManufacturer(manufacturer: string): DriveSpec[] {
  return DRIVE_DATABASE.filter(drive => drive.manufacturer === manufacturer);
}

// Helper function to get drives by interface
export function getDrivesByInterface(intf: 'SATA' | 'SAS' | 'NVMe'): DriveSpec[] {
  return DRIVE_DATABASE.filter(drive => drive.interface === intf);
}

// Helper function to get drives by type
export function getDrivesByType(type: 'HDD' | 'SSD'): DriveSpec[] {
  return DRIVE_DATABASE.filter(drive => drive.type === type);
}
